/**
 * Every live grant that reaches one program — the program's own scope node
 * plus its ancestor chain. Tenant-root grants are surfaced as a count only:
 * dozens of org-wide rows would bury the program's own story. Backed by
 * ryugu's ListProgramGrants (SAD-99), which is authorized on manage_members
 * (program lead / delivery VP), not baseline admin/internal — the RED pass
 * showed a real Program Lead getting 403s from the old admin-only
 * AdminListAssignmentsForScopeNode/AdminGetScopeTree path this hook used to
 * call, so grants and ancestor-chain resolution now happen server-side.
 */ import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { deliveryClient } from '../../../../lib/connect/clients';
export const SCOPE_GRANTS_KEY = 'program-access-grants';
/** Where a grant came from, in customer words. */ export function grantSource(a) {
    if (a.reason.startsWith('roster:')) return 'team-role';
    if (a.assignmentType === 'designee') return 'delegation';
    if (a.assignmentType === 'break_glass') return 'break-glass';
    return 'direct';
}
export const GRANT_SOURCE_LABEL = {
    'team-role': 'Team role',
    delegation: 'Delegation',
    'break-glass': 'Emergency',
    direct: 'Granted directly'
};
export function useProgramGrants(programId, enabled) {
    const query = useQuery({
        queryKey: [
            SCOPE_GRANTS_KEY,
            programId
        ],
        enabled: enabled && !!programId,
        staleTime: 30000,
        queryFn: async ()=>deliveryClient.listProgramGrants({
                programId
            })
    });
    const rows = useMemo(()=>(query.data?.grants ?? []).map((assignment)=>({
                assignment,
                grantedOnNodeId: assignment.scopeNodeId,
                inherited: assignment.inherited
            })), [
        query.data
    ]);
    const nodeNameById = useMemo(()=>{
        const m = new Map();
        for (const g of query.data?.grants ?? [])m.set(g.scopeNodeId, g.scopeNodeName);
        return m;
    }, [
        query.data
    ]);
    const nodeName = (id)=>nodeNameById.get(id) || id;
    // Server-resolved (ryugu's stampGrantDisplayNames, SAD-99): a Program
    // Lead/Delivery VP has no access to the admin-only name resolver
    // (useAdminNames), so these are the only names this hook's caller has for
    // a directly-granted user, a group, or a delegation's original owner.
    // Best-effort — empty when the lookup missed — callers fall back further.
    const userNameById = useMemo(()=>{
        const m = new Map();
        for (const g of query.data?.grants ?? []){
            if (g.userId && g.userDisplayName) m.set(g.userId, g.userDisplayName);
            if (g.originalOwnerUserId && g.originalOwnerDisplayName) {
                m.set(g.originalOwnerUserId, g.originalOwnerDisplayName);
            }
        }
        return m;
    }, [
        query.data
    ]);
    const groupNameById = useMemo(()=>{
        const m = new Map();
        for (const g of query.data?.grants ?? []){
            if (g.groupId && g.groupDisplayName) m.set(g.groupId, g.groupDisplayName);
        }
        return m;
    }, [
        query.data
    ]);
    const userName = (id)=>userNameById.get(id);
    const groupName = (id)=>groupNameById.get(id);
    return {
        rows,
        orgWideCount: query.data?.orgWideGrantCount ?? 0,
        nodeName,
        userName,
        groupName,
        isLoading: enabled && query.isLoading
    };
}
